import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["isHidden"];
import PropTypes from 'prop-types';
import React, { forwardRef, Fragment, useCallback } from 'react';
import { Anchor } from '../Anchor/Anchor';
import { withUncontrolled } from '../ShowHide/withUncontrolled';
var getUpdatedContent = function getUpdatedContent(wordCount, content) {
  var arr = content.split(' ');
  if (arr.length < wordCount) {
    return content;
  }
  return arr.slice(0, wordCount).join(' ').concat(' ...');
};

/**
 * A `ShowHideWordCount` is used when the content is a string type and
 * an automatic truncation based on word count is preferred.
 *
 * If content is not a string type, e.g. containing `Anchor`, `span`, `b`, etc.,
 * please refer to `ShowHide`.
 *
 * W3C uses [`Disclosure`](https://www.w3.org/TR/wai-aria-practices-1.1/#disclosure)
 * to refer similar behavior.
 *
 * @since 8.36.0
 */
export var ShowHideWordCount = withUncontrolled( /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var text = _ref.text,
    renderButton = _ref.renderButton,
    isHidden = _ref.isHidden,
    onClick = _ref.onClick,
    wordCount = _ref.wordCount;
  /**
   * EVENT HANDLER
   */
  var onClickCallback = useCallback(function () {
    if (onClick) {
      onClick(isHidden);
    }
  }, [isHidden, onClick]);

  /**
   * RENDER
   */

  // render toggle button
  var button = renderButton;
  if (typeof renderButton === 'function') {
    button = renderButton({
      isHidden: isHidden,
      ref: forwardedRef,
      onClick: onClickCallback
    });
  }

  // render children
  var content = text;
  if (isHidden) {
    content = getUpdatedContent(wordCount, content);
  }
  return /*#__PURE__*/React.createElement(Fragment, null, content, " ", " ", " ", button);
}));
ShowHideWordCount.displayName = 'ShowHideWordCount';
ShowHideWordCount.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A ShowHide is uncontrolled by default.
   * You can specify `isHidden` to manually control the visibility of disclosable content.
   */
  // This prop will be added in withUncontrolled but will need to be exposed in the doc.
  defaultHidden: PropTypes.bool,
  /**
   * A ShowHide is uncontrolled by default.
   * You can specify `isHidden` to manually control the visibility of disclosable content.
   */
  isHidden: PropTypes.bool,
  /**
   * Function called when button is pressed.
   */
  onClick: PropTypes.func,
  /**
   * Function called when button is pressed.
   */
  wordCount: PropTypes.number,
  /**
   * Default button styling corresponding to the `appearance` prop.
   */
  renderButton: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Content to be displayed. Part of the content will be toggled
   * based on `wordCount`.
   */
  text: PropTypes.string.isRequired
} : {};
ShowHideWordCount.defaultProps = {
  defaultHidden: true,
  wordCount: 100,
  renderButton: function renderButton(_ref2) {
    var isHidden = _ref2.isHidden,
      props = _objectWithoutProperties(_ref2, _excluded);
    return /*#__PURE__*/React.createElement(Anchor, _extends({
      as: "button",
      "aria-expanded": !isHidden
    }, props), isHidden ? 'See more' : 'See less');
  }
};